import styles from "./tracking-map.module.css";
import type { MapPingType } from "./tracking-map.tsx";

const EARTH_CIRCUMFERENCE = 40_075_016.686; // meters
const TILE_SIZE = 512; // MapLibre GL / Trimble Maps tile size
const PRIVACY_RADIUS_METERS = 1100;
const MIN_DOT_SIZE = 12; // minimum px so dots stay visible when zoomed out
const MAX_OPACITY = 1;
const MIN_OPACITY = 0.25;

function readCssVar(name: string): string {
	return getComputedStyle(document.documentElement)
		.getPropertyValue(name)
		.trim();
}

export function getMarkerColor(type: MapPingType): string {
	return type === "tracking"
		? readCssVar("--kendo-color-primary")
		: readCssVar("--kendo-color-primary-on-subtle");
}

export function getDotScale(
	zoom: number,
	latitude: number,
): { size: number; opacity: number } {
	const metersPerPixel =
		(Math.cos((latitude * Math.PI) / 180) * EARTH_CIRCUMFERENCE) /
		(TILE_SIZE * 2 ** zoom);

	// Diameter in pixels that covers 1100m radius (2 * radius)
	const pixelDiameter = (PRIVACY_RADIUS_METERS * 2) / metersPerPixel;

	const size = Math.max(pixelDiameter, MIN_DOT_SIZE);

	// Opacity: fully opaque at min size, fades as circle grows
	const opacity =
		size <= MIN_DOT_SIZE
			? MAX_OPACITY
			: Math.max(MIN_OPACITY, MIN_DOT_SIZE / size);

	return { size, opacity };
}

function hexToRgba(hex: string, opacity: number): string {
	const r = Number.parseInt(hex.slice(1, 3), 16);
	const g = Number.parseInt(hex.slice(3, 5), 16);
	const b = Number.parseInt(hex.slice(5, 7), 16);
	return `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(opacity)})`;
}

export function applyDotScale(
	el: HTMLDivElement,
	zoom: number,
	latitude: number,
	color: string,
): void {
	const { size, opacity } = getDotScale(zoom, latitude);
	el.style.width = `${String(size)}px`;
	el.style.height = `${String(size)}px`;
	el.style.backgroundColor = hexToRgba(color, opacity);
}

export function createDotElement(color: string): HTMLDivElement {
	const el = document.createElement("div");
	el.className = styles.dotMarker;
	el.style.backgroundColor = color;
	return el;
}

export function createLabeledMarkerElement(
	color: string,
	text: string,
): HTMLDivElement {
	const wrapper = document.createElement("div");
	wrapper.className = styles.pinWrapper;

	const pin = document.createElement("div");
	pin.className = styles.pinMarker;
	pin.style.backgroundColor = color;

	const label = document.createElement("span");
	label.className = styles.pinLabel;
	label.textContent = text;

	pin.append(label);
	wrapper.append(pin);
	return wrapper;
}
